/**
 * O palco: onde as caras e os ecrãs aparecem.
 *
 * É a razão de existir do GreenVoice, por isso tem duas regras:
 *
 *  1. TODA A GENTE É VISÍVEL. Se cinco pessoas partilham ecrã, vêem-se os cinco
 *     ecrãs lado a lado — não um só "em destaque" e os outros escondidos. Ver o
 *     que cada um mostra ao mesmo tempo é o ponto de uma reunião de equipa.
 *  2. QUEM ASSISTE MANDA NO SEU LAYOUT. O modo "Livre" deixa cada espectador
 *     arrastar e redimensionar cada janela dentro do espaço da chamada — a
 *     minha disposição não mexe na tua. É estado local, por pessoa, efémero.
 *
 * O `<video>` é alimentado por `track.attach()` do LiveKit, nunca por um
 * `srcObject` montado à mão: o attach trata de `playsInline`, do autoplay e da
 * troca de track sem piscar — problemas reais que já estão resolvidos lá dentro.
 */

import type { Snowflake } from "@greenvoice/shared";
import type { Track } from "livekit-client";
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { micControl, useVoiceActions, useVoiceSnapshot, voiceLive, voiceProblem } from "../hooks/useVoice";
import { screenShareSupported } from "../lib/media";
import { type ParticipantView, voiceEngine } from "../lib/voice";
import { loadVoicePrefs, saveVoicePrefs, type VoicePrefs } from "../lib/voicePrefs";
import { memberColor, voiceParticipants } from "../store/selectors";
import { memberKey, useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { ReactionPicker, ReactionsOverlay } from "./CallReactions";
import { assistidosLembrados, lembrarAssistidos } from "./callWatching";
import { CallSettings } from "./CallSettings";
import {
  IconBoard,
  IconCamera,
  IconExpand,
  IconGrid,
  IconHand,
  IconHeadphones,
  IconHeadphonesOff,
  IconMic,
  IconMicOff,
  IconMove,
  IconPhoneOff,
  IconScreen,
  IconSettings,
  IconShrink,
  IconSmile,
  IconSpeaker,
  IconSpeakerOff,
} from "./Icons";
import { SignalIcon } from "./SignalIcon";
import { TOPBAR_TOOLS_SLOT_ID } from "./TopBar";
import { Whiteboard } from "./Whiteboard";

/**
 * As três disposições do palco.
 *
 * O `screens` já cá esteve: nasceu a 26-08 e morreu 42 minutos depois, com a
 * mensagem *"remove o modo 'Telas'/cinema (contrariava o opt-in)"* — mostrava a
 * tela de toda a gente sozinho, no mesmo dia em que o «Assistir tela» prometia
 * que ninguém entra na tela de ninguém sem clicar.
 *
 * Volta sem essa contradição, e a diferença é toda: o modo mostra QUE telas
 * existem, não o conteúdo delas. Cada uma abre-se com o seu próprio clique —
 * *"aparece todas as telas, mas precisa clicar em assistir tela da pessoa, se
 * quiser"*. É a lista de quem está a partilhar, não uma janela indiscreta.
 */
type StageMode = "auto" | "free" | "screens";

/** Posição/tamanho de uma janela em FRACÇÕES (0..1) do palco. Fracções e não
 *  pixéis de propósito: assim a disposição sobrevive a um redimensionamento da
 *  janela do browser sem as janelas saírem do sítio. */
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface StageTile {
  id: string;
  participant: ParticipantView;
  track: Track | null;
  kind: "camera" | "screen";
  label: string;
}

const MIN_W = 0.14;
const MIN_H = 0.16;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Arruma N janelas numa grelha que enche o palco — o ponto de partida do modo
 *  Livre e o resultado do botão "Reorganizar". A última fila alarga-se para não
 *  deixar buracos quando o número não é um quadrado perfeito. */
function autoTile(ids: string[]): Record<string, Rect> {
  const out: Record<string, Rect> = {};
  const n = ids.length;
  if (!n) return out;
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  ids.forEach((id, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const inRow = r === rows - 1 ? n - r * cols : cols;
    const w = 1 / inRow;
    const h = 1 / rows;
    out[id] = { x: c * w, y: r * h, w, h };
  });
  return out;
}

export function VoiceStage({
  channelId,
  shortcuts = true,
  toolsInBar = false,
}: {
  channelId: Snowflake;
  // Atalhos de tecla crua (M/D/V, premir-para-falar) só quando a chamada É a
  // superfície — a vista de canal. No PIP dentro do escritório o jogo é o dono
  // do teclado (WASD anda, `D` = direita) e sobrepor-lhe `D` = ensurdecer fazia
  // o micro e os phones ligarem-se sozinhos a cada passo. Lá, os botões mandam.
  shortcuts?: boolean;
  /**
   * A disposição do palco (Grade / Livre) sobe para a barra de cima em vez de
   * ficar POR CIMA do vídeo — *"mova o grade e livre pro header de cima"*.
   *
   * É uma pergunta de contexto e não de gosto: aqueles botões mandam no ecrã
   * inteiro, e onde o palco É o ecrã inteiro eles pertencem à barra que também
   * o é. No cartão de canto do escritório ficam onde estão: ali o palco tem
   * 300px e a barra de cima é do MUNDO, não daquela janelinha.
   */
  toolsInBar?: boolean;
}) {
  const state = useStore();
  const snapshot = useVoiceSnapshot();
  const actions = useVoiceActions();

  const [mode, setMode] = useState<StageMode>("auto");
  const [custom, setCustom] = useState<Record<string, Rect>>({});
  const [board, setBoard] = useState(false);
  const [panel, setPanel] = useState<null | "react" | "settings">(null);
  const [spotlightId, setSpotlightId] = useState<string | null>(null);
  // Assistir tela é OPT-IN: guardo QUEM estou a ver (a identity de quem partilha)
  // e se está em tela cheia. Sem isto, entrava-se na tela de alguém sozinho — é
  // justamente o que não queremos. Estado local e por espectador: eu decidir ver
  // a tua tela não obriga mais ninguém a vê-la. Não há posição/tamanho da tela: a
  // miniatura É o palco (no escritório, o PIP do canto) — mover/redimensionar
  // faz-se movendo/redimensionando o PIP, não uma segunda janela.
  // Um CONJUNTO e não um só: no modo Telas as telas estão todas à vista e cada
  // uma abre com o seu clique, portanto podem estar várias abertas ao mesmo
  // tempo. Guardam-se as IDENTIDADES (não os tiles): quem pára de partilhar
  // desaparece da lista sozinho, sem estado preso a uma tela que já não existe.
  // ...e a lista SOBREVIVE à desmontagem: carregar em «Discord» desmonta o
  // escritório inteiro, e com ele este palco. Nascer a `[]` era o que fechava
  // as telas de quem só foi ver o chat e voltou — o botão «Assistir tela» a
  // pedir outra vez o que já tinha sido pedido. A memória é por canal e vive
  // fora do React (`callWatching`), como a posição do corpo no mapa.
  const [watchingIds, setWatchingIds] = useState<string[]>(() => [...assistidosLembrados(channelId)]);
  // Qual delas está em tela cheia, se alguma. Era um booleano quando só havia
  // uma tela possível; com várias, "cheia" tem de dizer QUAL.
  const [fullId, setFullId] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<VoicePrefs>(loadVoicePrefs);
  const boxRef = useRef<HTMLDivElement>(null);

  // O lugar da barra de cima, quando a disposição do palco sobe para lá. Como no
  // `ChatView`: a barra é IRMÃ deste componente, não antepassada, por isso
  // procura-se o nó por id depois de montar. Depende do `toolsInBar` porque a
  // resposta muda com ele — o cartão de canto não pendura nada.
  const [toolsSlot, setToolsSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setToolsSlot(toolsInBar ? document.getElementById(TOPBAR_TOOLS_SLOT_ID) : null);
  }, [toolsInBar]);

  // `actions` é um objecto novo a cada render (callbacks memoizados lá dentro).
  // Guardá-lo numa ref deixa os handlers de teclado ligarem-se UMA vez em vez de
  // re-subscreverem a cada render — sem isto o listener global recriava-se sempre.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const here = state.voice?.channelId === channelId;
  // `voiceLive` e não `=== "connected"`: a recuperar continua a ser uma chamada,
  // e é precisamente quando a rede treme que se quer poder fechar o microfone.
  const connected = here && voiceLive(snapshot);
  const capabilities = state.voice?.capabilities;
  const mic = micControl(snapshot, capabilities);
  // O erro do MOTOR e o erro do JOIN, na mesma linha do ecrã. Só o segundo era
  // mostrado, e o primeiro é o que aparece quando o microfone se recusa a abrir.
  const problem = voiceProblem(snapshot, state.voice?.error);
  // Uma prova de existência da API, não um palpite pelo user-agent — e não muda
  // durante a vida da página, por isso lê-se no render sem estado nenhum.
  const canCaptureScreen = screenShareSupported();

  const updatePrefs = useCallback((next: VoicePrefs) => {
    setPrefs(next);
    saveVoicePrefs(next);
  }, []);

  // Atalhos de teclado: M mudo, D ensurdecer, V câmara — o vocabulário que quem
  // vem do Discord/Meet já tem nos dedos. E o premir-para-falar: manter a tecla
  // abre o micro, soltar fecha-o. Ignora-se tudo enquanto se escreve num campo
  // (senão escrever "movimento" no chat silenciava a chamada) e com modificadores
  // (Ctrl/Cmd/Alt são atalhos do browser/SO, não nossos). E NÃO se liga de todo
  // quando `shortcuts` é falso — no PIP do escritório o `D` de andar não pode
  // ensurdecer a chamada (era o bug do "micro e phones a ligar-se ao caminhar").
  useEffect(() => {
    if (!connected || !shortcuts) return;
    const isTyping = () => {
      const el = document.activeElement as HTMLElement | null;
      return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    };
    const canSpeak = () => useStore.getState().voice?.capabilities?.canSpeak !== false;
    const down = (event: KeyboardEvent) => {
      if (isTyping() || event.metaKey || event.ctrlKey || event.altKey) return;
      if (prefs.pushToTalk && event.code === prefs.pttKey) {
        event.preventDefault();
        // `repeat` = tecla a auto-repetir enquanto segura; só o primeiro toque abre.
        if (!event.repeat && canSpeak()) void actionsRef.current.pushTalk(true);
        return;
      }
      if (event.repeat) return;
      if (event.code === "KeyM") {
        event.preventDefault();
        // A mesma guarda do botão. Sem ela, o `M` abria o microfone com o botão
        // cinzento ao lado — duas respostas diferentes à mesma pergunta.
        if (canSpeak()) void actionsRef.current.toggleMic();
      } else if (event.code === "KeyD") {
        event.preventDefault();
        void actionsRef.current.toggleDeaf();
      } else if (event.code === "KeyV") {
        event.preventDefault();
        void actionsRef.current.toggleCamera();
      }
    };
    const up = (event: KeyboardEvent) => {
      if (!prefs.pushToTalk || event.code !== prefs.pttKey) return;
      event.preventDefault();
      if (canSpeak()) void actionsRef.current.pushTalk(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [connected, prefs, shortcuts]);

  // Ligar o premir-para-falar com o micro aberto silencia-o já: senão ficava
  // ligado e a tecla nunca chegava a ter efeito visível. Só dispara na transição.
  // Sem `shortcuts` (PIP do escritório) a tecla de fala nem existe, por isso não
  // se auto-silencia — deixava o micro fechado sem forma de o abrir a não ser o
  // botão, e o botão é um toggle simples, não o modelo do premir-para-falar.
  useEffect(() => {
    if (connected && shortcuts && prefs.pushToTalk && voiceEngine.getSnapshot().micEnabled) {
      void actionsRef.current.pushTalk(false);
    }
  }, [connected, prefs.pushToTalk, shortcuts]);

  // Guardar a escolha assim que ela muda — incluindo quando a lista se esvazia
  // porque alguém parou de partilhar. Um efeito e não um `set` embrulhado:
  // `setWatchingIds` é chamado de meia dúzia de sítios, e embrulhar cada um era
  // deixar o próximo por embrulhar.
  useEffect(() => {
    lembrarAssistidos(channelId, watchingIds);
  }, [channelId, watchingIds]);

  // Voltar a uma chamada DIFERENTE traz a escolha dessa chamada, não a da
  // anterior. Sem isto, mudar de sala levava as telas da sala antiga atrás.
  const canalAnterior = useRef(channelId);
  useEffect(() => {
    if (canalAnterior.current === channelId) return;
    canalAnterior.current = channelId;
    setWatchingIds([...assistidosLembrados(channelId)]);
    setFullId(null);
  }, [channelId]);

  // Quem eu estava a assistir parou de partilhar (ou saiu da chamada)? Fecho-lhe
  // a janela sozinho — uma tela que já não existe seria um retângulo preto preso
  // no ecrã. É um efeito, não um cálculo no render, porque muda ESTADO, e corre
  // antes do return por ser um hook.
  //
  // As LISTAS só se reescrevem quando alguma coisa saiu mesmo: um `filter` que
  // devolvesse sempre um array novo dava um render por cada frame de snapshot.
  useEffect(() => {
    const aPartilhar = new Set(snapshot.participants.filter((p) => p.screen).map((p) => p.identity));
    setWatchingIds((atual) =>
      atual.every((id) => aPartilhar.has(id)) ? atual : atual.filter((id) => aPartilhar.has(id)),
    );
    setFullId((atual) => (atual === null || aPartilhar.has(atual) ? atual : null));
  }, [snapshot.participants]);

  // Fora da chamada mostra-se quem lá está — a decisão de entrar toma-se a ver
  // quem está lá dentro, não às escuras.
  if (!here) {
    return <Lobby channelId={channelId} onJoin={() => void actions.join(channelId)} />;
  }

  const nameOf = (identity: string): string => {
    const user = state.users[identity];
    const member = state.activeServerId ? state.members[memberKey(state.activeServerId, identity)] : undefined;
    return member?.nickname ?? user?.displayName ?? user?.username ?? "Alguém";
  };

  // Uma cara por pessoa no palco; as telas partilhadas ficam à parte (`screens`)
  // e NÃO entram no palco sozinhas — vêem-se só a pedido, numa miniatura móvel.
  // Ninguém abre a tela de ninguém sem clicar "Assistir tela" (ver `watchProps`).
  const screens: StageTile[] = snapshot.participants
    .filter((p) => p.screen)
    .map((p) => ({
      id: `${p.identity}:screen`,
      participant: p,
      track: p.screen,
      kind: "screen",
      label: `${nameOf(p.identity)} — tela`,
    }));
  const faces: StageTile[] = snapshot.participants.map((p) => ({
    id: `${p.identity}:camera`,
    participant: p,
    track: p.camera,
    kind: "camera",
    label: nameOf(p.identity),
  }));
  // No palco ficam só as caras; quem partilha ganha um botão "Assistir tela" no
  // seu próprio tile (ver `watchProps`), e a tela abre-se à parte na miniatura.
  const tiles = faces;

  const me = voiceEngine.localId;
  const handRaised = me ? snapshot.raisedHands.includes(me) : false;
  const handOf = (identity: string) => snapshot.raisedHands.includes(identity);
  // O destaque só faz sentido em Grelha (no Livre cada um arruma à mão). Se a
  // pessoa destacada sair, o `find` devolve null e caímos na grelha normal — sem
  // estado preso a um tile que já não existe.
  const spotlightTile = spotlightId ? (tiles.find((t) => t.id === spotlightId) ?? null) : null;
  const toggleSpotlight = (id: string) => setSpotlightId((current) => (current === id ? null : id));

  // Quem está a partilhar tela agora, e o tile que a miniatura vai mostrar (null
  // se não estou a assistir a ninguém, ou se quem eu via já parou/saiu).
  const sharingIds = new Set(screens.map((s) => s.participant.identity));
  // As telas que EU abri e continuam a existir. O filtro sobre `screens` é o que
  // as fecha sozinhas quando alguém pára de partilhar ou sai.
  const assistidas = screens.filter((t) => watchingIds.includes(t.participant.identity));
  const telaCheia = fullId ? (assistidas.find((t) => t.participant.identity === fullId) ?? null) : null;
  const assistir = (identity: string) =>
    setWatchingIds((atual) => (atual.includes(identity) ? atual : [...atual, identity]));
  const parar = (identity: string) => {
    setWatchingIds((atual) => atual.filter((id) => id !== identity));
    setFullId((atual) => (atual === identity ? null : atual));
  };

  /**
   * No modo LIVRE a tela que se assiste deixa de ser um manto e passa a ser mais
   * uma JANELA — e é por isso que ela entra aqui, na conta das posições.
   *
   * Era este o *"a opção livre não está funcionando?"*: a tela é
   * `position: absolute; inset: 0` dentro do palco, portanto tapava a área toda.
   * O modo mudava, os tiles por baixo ficavam mesmo arrastáveis — só que
   * invisíveis e fora de alcance, debaixo do ecrã partilhado. Carregar em Livre
   * não fazia nada VISÍVEL, que para quem usa é o mesmo que não fazer nada.
   *
   * Na Grelha continua manto: ali a promessa é o contrário — a tela enche o
   * palco e as caras ficam à volta.
   */
  const telasLivres = mode === "free" ? assistidas.filter((t) => t !== telaCheia) : [];
  // O manto: no Livre não há (são janelas), no Telas não há (a grelha É delas).
  const telasNoManto = mode === "auto" ? assistidas.filter((t) => t !== telaCheia) : [];

  // Grelha automática para todos + a fracção custom por cima de quem foi movido.
  // Uma janela nova entra já num lugar da grelha; uma movida mantém o seu.
  const tileIds = [...tiles.map((t) => t.id), ...telasLivres.map((t) => t.id)];
  const positions: Record<string, Rect> = { ...autoTile(tileIds), ...custom };
  const setRect = (id: string, rect: Rect) => setCustom((prev) => ({ ...prev, [id]: rect }));
  // O que cada cara recebe quando está a partilhar tela. Se ainda não a estou a
  // ver, um botão para COMEÇAR a ver — inclusive na minha própria (preview, como
  // no Discord: confirmo que estou a transmitir o ecrã certo). No meu tile o
  // botão diz "Ver a minha tela" e mantém o selo "A partilhar". Quando já a estou
  // a ver, fica só o selo de estado.
  const watchProps = (p: ParticipantView): { onWatch?: () => void; watchLabel?: string; shareBadge?: string } => {
    if (!sharingIds.has(p.identity)) return {};
    if (watchingIds.includes(p.identity)) {
      return { shareBadge: p.isLocal ? "A partilhar" : "A assistir" };
    }
    const open = () => assistir(p.identity);
    return p.isLocal ? { onWatch: open, watchLabel: "Ver a minha tela", shareBadge: "A partilhar" } : { onWatch: open };
  };

  return (
    <div className="stage">
      {snapshot.needsAudioUnlock ? (
        <button type="button" className="stage__unlock" onClick={() => void actions.unlockAudio()}>
          O browser bloqueou o som — clique aqui para ouvir.
        </button>
      ) : null}

      {problem ? (
        <p className="stage__error" role="alert">
          {problem}
        </p>
      ) : null}

      {snapshot.state === "reconnecting" ? <p className="stage__notice">Recuperando a conexão…</p> : null}

      {tiles.length > 0
        ? (() => {
            const ferramentas = (
              <div
                className={`stage__tools${toolsSlot ? " stage__tools--bar" : ""}`}
                role="toolbar"
                aria-label="Disposição do palco"
              >
                <button
                  type="button"
                  className={`stage__tool${mode === "auto" ? " is-active" : ""}`}
                  onClick={() => setMode("auto")}
                  title="Grade automática"
                >
                  <IconGrid size={15} />
                  <span>Grade</span>
                </button>
                <button
                  type="button"
                  className={`stage__tool${mode === "free" ? " is-active" : ""}`}
                  onClick={() => setMode("free")}
                  title="Arrastar e redimensionar cada janela"
                >
                  <IconMove size={15} />
                  <span>Livre</span>
                </button>
                <button
                  type="button"
                  className={`stage__tool${mode === "screens" ? " is-active" : ""}`}
                  onClick={() => setMode("screens")}
                  title="Só as telas partilhadas — cada uma abre com o seu «Assistir tela»"
                >
                  <IconScreen size={15} />
                  <span>Telas</span>
                </button>
                {mode === "free" ? (
                  <button
                    type="button"
                    className="stage__tool"
                    onClick={() => setCustom({})}
                    title="Voltar a arrumar em grade"
                  >
                    Reorganizar
                  </button>
                ) : null}
              </div>
            );
            // Na barra de cima ou por cima do vídeo — o mesmo desenho, dois
            // sítios. Sem slot (o cartão de canto, um telemóvel sem barra) cai
            // no sítio de sempre em vez de desaparecer.
            return toolsSlot ? createPortal(ferramentas, toolsSlot) : ferramentas;
          })()
        : null}

      <div className={`stage__body${mode === "free" ? " stage__body--free" : ""}`} ref={boxRef}>
        {mode === "screens" ? (
          /*
           * A LISTA de quem está a partilhar — e é uma lista, não um espelho: as
           * que ainda não abri mostram o cartão com o botão, e só o clique as
           * põe a andar. É o que separa este modo do que foi removido em Agosto,
           * que abria a tela de toda a gente sem ninguém pedir.
           */
          screens.length > 0 ? (
            <div className="stage__cinema" data-count={Math.min(screens.length, 3)}>
              {screens.map((tela) =>
                watchingIds.includes(tela.participant.identity) && tela !== telaCheia ? (
                  <ScreenView
                    key={tela.id}
                    name={nameOf(tela.participant.identity)}
                    track={tela.track}
                    onToggleFull={() => setFullId(tela.participant.identity)}
                    onStop={() => parar(tela.participant.identity)}
                  />
                ) : (
                  <TelaPorAbrir
                    key={tela.id}
                    name={nameOf(tela.participant.identity)}
                    aVer={tela === telaCheia}
                    propria={tela.participant.isLocal}
                    onWatch={() => assistir(tela.participant.identity)}
                  />
                ),
              )}
            </div>
          ) : (
            <p className="stage__cinema-vazio">Ninguém está a partilhar tela agora.</p>
          )
        ) : mode === "free" ? (
          <>
            {tiles.map((tile) => (
              <MovableTile
                key={tile.id}
                rect={positions[tile.id] ?? { x: 0, y: 0, w: 0.4, h: 0.4 }}
                onChange={(rect) => setRect(tile.id, rect)}
                boxRef={boxRef}
                label={tile.label}
              >
                <VideoTile
                  participant={tile.participant}
                  track={tile.track}
                  kind={tile.kind}
                  raisedHand={handOf(tile.participant.identity)}
                  {...watchProps(tile.participant)}
                />
              </MovableTile>
            ))}
            {telasLivres.map((tela) => (
              <MovableTile
                key={tela.id}
                rect={positions[tela.id] ?? { x: 0.2, y: 0.2, w: 0.6, h: 0.6 }}
                onChange={(rect) => setRect(tela.id, rect)}
                boxRef={boxRef}
                label={tela.label}
              >
                {(pega) => (
                  <ScreenView
                    name={nameOf(tela.participant.identity)}
                    track={tela.track}
                    arrasto={pega}
                    onToggleFull={() => setFullId(tela.participant.identity)}
                    onStop={() => parar(tela.participant.identity)}
                  />
                )}
              </MovableTile>
            ))}
          </>
        ) : spotlightTile ? (
          // Destaque: um grande + os restantes numa fita ao lado. Duplo-clique
          // num tile (ou o botão de recolher) troca ou sai do destaque.
          <div className="stage__spot">
            <div className="stage__spot-main">
              <VideoTile
                participant={spotlightTile.participant}
                track={spotlightTile.track}
                kind={spotlightTile.kind}
                raisedHand={handOf(spotlightTile.participant.identity)}
                spotlighted
                onToggleSpotlight={() => toggleSpotlight(spotlightTile.id)}
                {...watchProps(spotlightTile.participant)}
              />
            </div>
            {tiles.length > 1 ? (
              <div className="stage__spot-strip">
                {tiles
                  .filter((tile) => tile.id !== spotlightTile.id)
                  .map((tile) => (
                    <VideoTile
                      key={tile.id}
                      participant={tile.participant}
                      track={tile.track}
                      kind={tile.kind}
                      raisedHand={handOf(tile.participant.identity)}
                      onToggleSpotlight={() => toggleSpotlight(tile.id)}
                      {...watchProps(tile.participant)}
                    />
                  ))}
              </div>
            ) : null}
          </div>
        ) : (
          // `data-count` deixa o CSS tratar a call de UMA pessoa como o caso
          // próprio que é: um cartão centrado, e não um tile 16/9 esticado à
          // largura do palco a deixar uma mancha preta à volta.
          <div className="stage__grid" data-count={faces.length}>
            {faces.map((tile) => (
              <VideoTile
                key={tile.id}
                participant={tile.participant}
                track={tile.track}
                kind="camera"
                raisedHand={handOf(tile.participant.identity)}
                onToggleSpotlight={() => toggleSpotlight(tile.id)}
                {...watchProps(tile.participant)}
              />
            ))}
          </div>
        )}

        {/* A tela de quem assisto VAI PARA DENTRO do palco — logo, dentro do PIP
            do escritório (o cartão de canto), não numa janela à parte. Enche o
            corpo por cima das caras, com cabeçalho a dizer de quem é e a trazer
            parar/tela-cheia. Em "tela cheia" sobe a `document.body` (bloco no fim
            do componente). Sem clicar "Assistir tela" nada disto existe — opt-in. */}
        {telasNoManto.length > 0 ? (
          <div className="stage__manto" data-count={Math.min(telasNoManto.length, 3)}>
            {telasNoManto.map((tela) => (
              <ScreenView
                key={tela.id}
                name={nameOf(tela.participant.identity)}
                track={tela.track}
                onToggleFull={() => setFullId(tela.participant.identity)}
                onStop={() => parar(tela.participant.identity)}
              />
            ))}
          </div>
        ) : null}

        {connected ? <ReactionsOverlay nameOf={nameOf} /> : null}
        {connected ? <Whiteboard open={board} active={connected} onClose={() => setBoard(false)} /> : null}
      </div>

      {connected && panel ? (
        // O véu apanha o clique fora e fecha o balão; o balão em si fica por cima.
        <>
          <div className="call-pop__scrim" onClick={() => setPanel(null)} />
          <div className={`call-pop__anchor call-pop__anchor--${panel}`}>
            {panel === "react" ? (
              <ReactionPicker
                onPick={(emoji) => {
                  actions.react(emoji);
                  setPanel(null);
                }}
              />
            ) : (
              <CallSettings actions={actions} prefs={prefs} onPrefs={updatePrefs} onClose={() => setPanel(null)} />
            )}
          </div>
        </>
      ) : null}

      <div className="stage__controls">
        <div className="stage__mic">
          <button
            type="button"
            className={`round-button${snapshot.micEnabled ? "" : " is-off"}`}
            data-ctl="mic"
            disabled={mic.disabled}
            onClick={() => void actions.toggleMic()}
            title={mic.title}
          >
            {snapshot.micEnabled ? <IconMic size={20} /> : <IconMicOff size={20} />}
          </button>
          {/* O medidor responde à outra metade da pergunta — *"não sei se o som
              está saindo"* — e responde-a continuamente: se as barras mexem
              quando falas, o microfone está a captar. Um anel que acende é
              binário e, quando não acende, não diz se o problema é a voz, o
              limiar ou o microfone. */}
          {snapshot.micEnabled ? <MedidorDeVoz /> : null}
        </div>

        <button
          type="button"
          className={`round-button${snapshot.deafened ? " is-off" : ""}`}
          data-ctl="deaf"
          disabled={!connected}
          onClick={() => void actions.toggleDeaf()}
          title={snapshot.deafened ? "Voltar a ouvir" : "Ensurdecer"}
        >
          {snapshot.deafened ? <IconHeadphonesOff size={20} /> : <IconHeadphones size={20} />}
        </button>

        <button
          type="button"
          className={`round-button${snapshot.cameraEnabled ? " is-on" : ""}`}
          data-ctl="cam"
          disabled={!connected || capabilities?.canVideo === false}
          onClick={() => void actions.toggleCamera()}
          title={snapshot.cameraEnabled ? "Desligar câmera" : "Ligar câmera"}
        >
          <IconCamera size={20} />
        </button>

        {/* Partilhar ecrã tem dois donos e ambos podem dizer não: o servidor (é
            uma permissão de cargo) e o próprio aparelho. Num telemóvel não há
            `getDisplayMedia` — nem no Safari do iOS nem no Chrome do Android —
            e sem esta segunda pergunta o botão aparecia ligado e rebentava com
            um `TypeError` ao toque, que se lê como avaria da app. Desligado com
            uma frase honesta é a diferença entre "não dá" e "está partido". */}
        {/* VERMELHO a partilhar, e não verde como os outros ligados. Os dois
            estados não são a mesma pergunta: no microfone e na câmara o botão
            diz o que ESTÁ (ligado, verde); aqui o que interessa é o que ACONTECE
            ao carregar — corta a transmissão do meu ecrã a toda a gente. Um
            botão que desfaz uma coisa que os outros estão a ver pede a cor de
            quem desfaz, a mesma do desligar a chamada. */}
        <button
          type="button"
          className={`round-button${snapshot.screenEnabled ? " is-stop" : ""}`}
          data-ctl="screen"
          disabled={!connected || capabilities?.canShareScreen === false || !canCaptureScreen}
          onClick={() => void actions.toggleScreen()}
          title={
            canCaptureScreen
              ? snapshot.screenEnabled
                ? "Parar compartilhamento"
                : "Compartilhar tela"
              : "Este aparelho não permite partilhar o ecrã"
          }
        >
          <IconScreen size={20} />
        </button>

        {/* Só aparece quando estou a partilhar ecrã COM som: aí posso cortar o
            áudio da transmissão sem parar a imagem — ninguém ouve o que toca cá. */}
        {connected && snapshot.screenEnabled && snapshot.screenAudioAvailable ? (
          <button
            type="button"
            className={`round-button${snapshot.screenAudioMuted ? " is-off" : " is-on"}`}
            data-ctl="screenaudio"
            onClick={() => void actions.toggleScreenAudio()}
            title={
              snapshot.screenAudioMuted
                ? "Ativar o som do compartilhamento (voltam a ouvir)"
                : "Silenciar o som do compartilhamento (ninguém ouve)"
            }
          >
            {snapshot.screenAudioMuted ? <IconSpeakerOff size={20} /> : <IconSpeaker size={20} />}
          </button>
        ) : null}

        <button
          type="button"
          className={`round-button${handRaised ? " is-on" : ""}`}
          data-ctl="hand"
          disabled={!connected}
          onClick={() => actions.raiseHand(!handRaised)}
          title={handRaised ? "Baixar a mão" : "Levantar a mão"}
        >
          <IconHand size={20} />
        </button>

        <button
          type="button"
          className={`round-button${panel === "react" ? " is-on" : ""}`}
          data-ctl="react"
          disabled={!connected}
          onClick={() => setPanel((current) => (current === "react" ? null : "react"))}
          title="Reagir"
        >
          <IconSmile size={20} />
        </button>

        <button
          type="button"
          className={`round-button${board ? " is-on" : ""}`}
          data-ctl="board"
          disabled={!connected}
          onClick={() => setBoard((v) => !v)}
          title={board ? "Fechar quadro" : "Abrir quadro"}
        >
          <IconBoard size={20} />
        </button>

        <button
          type="button"
          className={`round-button${panel === "settings" ? " is-on" : ""}`}
          data-ctl="settings"
          disabled={!connected}
          onClick={() => setPanel((current) => (current === "settings" ? null : "settings"))}
          title="Configurações da chamada"
        >
          <IconSettings size={20} />
        </button>

        <button
          type="button"
          className="round-button round-button--danger"
          data-ctl="leave"
          onClick={() => void actions.leave()}
          title="Sair da chamada"
        >
          <IconPhoneOff size={20} />
        </button>
      </div>

      {/* Tela cheia: só aqui a tela escapa ao palco e sobe a `document.body`, por
          cima de tudo. A vista normal vive DENTRO do palco (ver `stage__body`),
          que no escritório é o próprio cartão de canto — nada de janela solta. */}
      {telaCheia
        ? createPortal(
            <ScreenView
              name={nameOf(telaCheia.participant.identity)}
              track={telaCheia.track}
              full
              onToggleFull={() => setFullId(null)}
              onStop={() => parar(telaCheia.participant.identity)}
            />,
            document.body,
          )
        : null}
    </div>
  );
}

function Lobby({ channelId, onJoin }: { channelId: Snowflake; onJoin: () => void }) {
  const state = useStore();
  const waiting = voiceParticipants(state, channelId);

  return (
    <div className="stage stage--lobby">
      <div className="lobby">
        <h2>{state.channels[channelId]?.name}</h2>
        {waiting.length ? (
          <>
            <p>{waiting.length === 1 ? "Está 1 pessoa na sala." : `Estão ${waiting.length} pessoas na sala.`}</p>
            <div className="lobby__faces">
              {waiting.map((voiceState) => {
                const user = state.users[voiceState.userId];
                return user ? <Avatar key={voiceState.userId} user={user} size={56} /> : null;
              })}
            </div>
          </>
        ) : (
          <p>Ainda não tem ninguém aqui.</p>
        )}
        <button type="button" className="button button--primary" onClick={onJoin}>
          Entrar na sala
        </button>
      </div>
    </div>
  );
}

/**
 * Uma janela do modo Livre: arrasta-se pela barra do topo e redimensiona-se pelo
 * canto. Trabalha em fracções do palco e usa Pointer Events com pointer-capture
 * — um só handler serve rato, trackpad e toque, e o capture segue o dedo mesmo
 * que ele saia da janelinha a meio do gesto.
 */
/** Os quatro `onPointer*` que fazem uma coisa arrastar. Quem os receber vira pega. */
interface PegaDeArrasto {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
}

function MovableTile({
  rect,
  onChange,
  boxRef,
  label,
  children,
}: {
  rect: Rect;
  onChange: (rect: Rect) => void;
  boxRef: React.RefObject<HTMLDivElement | null>;
  label: string;
  /**
   * O conteúdo — ou uma FUNÇÃO que recebe a pega e a põe onde quiser.
   *
   * Existe por causa da tela assistida: ela já tem uma barra de título própria,
   * com o nome à esquerda e os botões (tela cheia, parar) à direita. A faixa de
   * arrasto normal cobre o topo INTEIRO da janela, portanto ou tapava aqueles
   * botões, ou encolhia a um punho minúsculo que ninguém encontra — e quem vê
   * uma barra de título tenta arrastar por ela, não por um canto invisível. Com
   * a pega emprestada, a barra que PARECE de arrastar é mesmo a que arrasta.
   */
  children: ReactNode | ((pega: PegaDeArrasto) => ReactNode);
}) {
  const drag = useRef<{ mode: "move" | "resize"; px: number; py: number; start: Rect; bw: number; bh: number } | null>(
    null,
  );

  const begin = (mode: "move" | "resize") => (e: ReactPointerEvent<HTMLElement>) => {
    // Um botão dentro da pega continua a ser um botão: sem isto, emprestar a
    // pega à barra da tela fazia com que "tela cheia" e "parar" começassem um
    // arrasto em vez de fazerem o que dizem.
    if ((e.target as HTMLElement).closest("button")) return;
    const box = boxRef.current?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { mode, px: e.clientX, py: e.clientY, start: rect, bw: box.width, bh: box.height };
  };

  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / d.bw;
    const dy = (e.clientY - d.py) / d.bh;
    if (d.mode === "move") {
      onChange({
        ...d.start,
        x: clamp(d.start.x + dx, 0, 1 - d.start.w),
        y: clamp(d.start.y + dy, 0, 1 - d.start.h),
      });
    } else {
      onChange({
        ...d.start,
        w: clamp(d.start.w + dx, MIN_W, 1 - d.start.x),
        h: clamp(d.start.h + dy, MIN_H, 1 - d.start.y),
      });
    }
  };

  const end = (e: ReactPointerEvent<HTMLElement>) => {
    drag.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* o capture pode já ter saído — não é erro */
    }
  };

  return (
    <div
      className="tile-window"
      style={{
        left: `${rect.x * 100}%`,
        top: `${rect.y * 100}%`,
        width: `${rect.w * 100}%`,
        height: `${rect.h * 100}%`,
      }}
    >
      {typeof children === "function" ? null : (
        <div
          className="tile-window__grab"
          onPointerDown={begin("move")}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          title="Arrastar"
        >
          <IconMove size={12} />
          <span>{label}</span>
        </div>
      )}
      {typeof children === "function"
        ? children({ onPointerDown: begin("move"), onPointerMove: move, onPointerUp: end, onPointerCancel: end })
        : children}
      <div
        className="tile-window__resize"
        onPointerDown={begin("resize")}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        title="Redimensionar"
      />
    </div>
  );
}

/**
 * Uma tela que EXISTE mas que ainda não abri.
 *
 * É a peça que faz o modo Telas ser uma lista e não um espelho. Mostra que a
 * pessoa está a partilhar e oferece o clique — nada mais: nem imagem, nem
 * pré-visualização, nem um primeiro frame "só para dar ideia". A promessa é
 * literal, *"ninguém entra na tela de ninguém sem clicar"*, e uma amostra do
 * conteúdo já seria entrar.
 */
function TelaPorAbrir({
  name,
  aVer,
  propria,
  onWatch,
}: {
  name: string;
  aVer: boolean;
  propria: boolean;
  onWatch: () => void;
}) {
  return (
    <div className="tela-card">
      <IconScreen size={26} />
      <strong>{name}</strong>
      {aVer ? (
        // Está aberta, mas em tela cheia por cima de tudo — o lugar dela na
        // grelha fica com a marca em vez de um buraco.
        <span className="tela-card__aver">A assistir em tela cheia</span>
      ) : (
        <button type="button" className="tile__watch" onClick={onWatch}>
          <IconScreen size={16} />
          {/* A minha própria tela diz outra coisa — é uma pré-visualização para
              confirmar que estou a transmitir o ecrã certo, não "assistir" a
              alguém. A mesma palavra que o botão do meu tile usa. */}
          {propria ? "Ver a minha tela" : "Assistir tela"}
        </button>
      )}
    </div>
  );
}

/**
 * O medidor do MEU microfone — cinco barras que sobem com a voz.
 *
 * Fora do React de propósito: o nível muda dez vezes por segundo, e pô-lo no
 * estado seria re-renderizar o palco inteiro — com vídeo lá dentro — a 10Hz,
 * para animar cinco divs. Aqui lê-se o motor num intervalo próprio e escreve-se
 * uma variável CSS no nó; o reconciliador nunca é acordado.
 *
 * Existe porque um indicador binário não responde à pergunta que a pessoa faz.
 * "Não sei se o som está a sair" não se resolve com um anel que não acendeu —
 * um anel apagado tanto pode ser "não falaste alto" como "o microfone está
 * morto". Barras que mexem separam as duas em dois segundos.
 */
function MedidorDeVoz() {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const id = window.setInterval(() => {
      // A raiz alarga a parte de baixo da escala: a diferença entre calado e a
      // falar baixinho é o que interessa ver, e em linear essa parte fica toda
      // esmagada na primeira barra.
      const nivel = Math.min(1, Math.sqrt(voiceEngine.nivelDeVoz()) * 1.6);
      ref.current?.style.setProperty("--nivel", nivel.toFixed(3));
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  return (
    <span className="nivel" ref={ref} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <i key={i} style={{ ["--i" as string]: i }} />
      ))}
    </span>
  );
}

interface VideoTileProps {
  participant: ParticipantView;
  track: Track | null;
  kind: "camera" | "screen";
  raisedHand?: boolean;
  spotlighted?: boolean;
  onToggleSpotlight?: () => void;
  // Aquela pessoa está a partilhar tela e ainda não a estou a ver: um botão ao
  // meio do tile para COMEÇAR a ver. Sem isto, entrava-se na tela de alguém sem
  // ter clicado — é exactamente o que não queremos.
  onWatch?: () => void;
  // Texto do botão de assistir (default "Assistir tela"; a minha própria tela
  // diz "Ver a minha tela").
  watchLabel?: string;
  // Selo de estado quando não há botão: "A partilhar" (a minha) / "A assistir".
  shareBadge?: string;
}

function VideoTile({
  participant,
  track,
  kind,
  raisedHand,
  spotlighted,
  onToggleSpotlight,
  onWatch,
  watchLabel,
  shareBadge,
}: VideoTileProps) {
  const state = useStore();
  const element = useRef<HTMLVideoElement>(null);
  // O track que este `<video>` está MESMO a mostrar. Soltar pelo `track` da prop
  // não serve: quando a câmara se desliga a prop já vem `null` nesta render, o
  // `detach` não fazia nada, e o elemento morto ficava na lista do LiveKit.
  const showing = useRef<Track | null>(null);

  // Callback ref: o `<video>` do modo Livre monta/desmonta ao trocar de layout,
  // por isso o attach acompanha o nó real em vez de um efeito preso ao track.
  // Memoizado PELO TRACK de propósito — um ref-callback com identidade nova a
  // cada render faz o React chamá-lo com `null` e logo com o nó outra vez, ou
  // seja detach+attach (srcObject a zero e de volta) sempre que alguém fala.
  const attach = useCallback(
    (node: HTMLVideoElement | null) => {
      if (element.current && showing.current && element.current !== node) {
        showing.current.detach(element.current);
        showing.current = null;
      }
      element.current = node;
      if (node && track) {
        track.attach(node);
        showing.current = track;
      }
    },
    [track],
  );

  const user = state.users[participant.identity];
  const serverId = state.activeServerId;
  const member = serverId ? state.members[memberKey(serverId, participant.identity)] : undefined;
  const color = memberColor(state, member);
  const name = member?.nickname ?? user?.displayName ?? user?.username ?? "Alguém";

  return (
    <div
      className={`tile tile--${kind}${participant.speaking ? " is-speaking" : ""}${spotlighted ? " is-spotlight" : ""}`}
      data-identity={participant.identity}
      // Duplo-clique destaca/recolhe — o gesto que toda a gente tenta primeiro.
      onDoubleClick={onToggleSpotlight}
    >
      {track ? (
        <video
          ref={attach}
          className="tile__video"
          autoPlay
          playsInline
          // Sempre mudo: o motor de voz ancora TODO o áudio (microfones e som
          // do ecrã partilhado) num sink próprio. Som aqui seria áudio a dobrar.
          muted
        />
      ) : (
        <div className="tile__idle">
          {user ? <Avatar user={user} size={72} speaking={participant.speaking} /> : null}
        </div>
      )}

      {shareBadge ? <span className="tile__live">{shareBadge}</span> : null}
      {onWatch ? (
        <button type="button" className="tile__watch" onClick={onWatch}>
          <IconScreen size={16} />
          {watchLabel ?? "Assistir tela"}
        </button>
      ) : null}

      {raisedHand ? (
        <span className="tile__hand" title={`${name} levantou a mão`} aria-label={`${name} levantou a mão`}>
          <IconHand size={15} />
        </span>
      ) : null}

      {onToggleSpotlight ? (
        <button
          type="button"
          className="tile__spot"
          onClick={onToggleSpotlight}
          title={spotlighted ? "Sair do destaque" : "Destacar"}
        >
          {spotlighted ? <IconShrink size={15} /> : <IconExpand size={15} />}
        </button>
      ) : null}

      <footer className="tile__bar">
        {participant.micEnabled ? null : <IconMicOff size={14} />}
        <span style={color ? { color } : undefined}>
          {name}
          {kind === "screen" ? " — tela" : ""}
        </span>
        <SignalIcon quality={participant.quality} identity={participant.identity} />
      </footer>
    </div>
  );
}

/**
 * A tela de quem se está a assistir — o "ver a tela" DENTRO do palco.
 *
 * NÃO é uma janela solta: renderiza-se DENTRO do palco (`absolute;
 * inset: 0` sobre as caras) e, como o palco no escritório é o cartão de canto, a
 * tela cai exactamente na miniatura — sem arrastar uma segunda janela. O botão de
 * tela cheia sobe-a a `document.body` (`full`, via portal em VoiceStage); o ✕ pára
 * de assistir. Mover/redimensionar a miniatura = mover/redimensionar o PIP (é ele
 * a miniatura). O vídeo vai MUDO: o som da partilha já toca no sink do motor de
 * voz (como em qualquer tile); som aqui seria áudio a dobrar.
 */
function ScreenView({
  name,
  track,
  full = false,
  arrasto,
  onToggleFull,
  onStop,
}: {
  name: string;
  track: Track | null;
  full?: boolean;
  /** A pega emprestada pela janela que a contém (modo Livre). Sem ela, a barra
   *  é só um título — que é o que ela é como manto do palco ou em tela cheia. */
  arrasto?: PegaDeArrasto;
  onToggleFull: () => void;
  onStop: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);

  // Attach preso ao TRACK e não a um callback-ref: a mesma tela pode passar a
  // mostrar quem partilha outra pessoa (troquei de quem assisto) sem se desmontar,
  // e aí o callback-ref não voltaria a disparar com o nó igual. O cleanup solta o
  // track anterior antes de agarrar o novo.
  useEffect(() => {
    const node = video.current;
    if (!node || !track) return;
    track.attach(node);
    return () => {
      track.detach(node);
    };
  }, [track]);

  return (
    <div className={`screen-view${full ? " screen-view--full" : ""}`}>
      <div
        className={`screen-view__bar${arrasto ? " screen-view__bar--pega" : ""}`}
        title={arrasto ? "Arrastar" : undefined}
        {...arrasto}
      >
        <span className="screen-view__name">{name} — tela</span>
        <div className="screen-view__actions">
          <button
            type="button"
            className="screen-view__btn"
            onClick={onToggleFull}
            title={full ? "Voltar à miniatura" : "Tela cheia"}
            aria-label={full ? "Voltar à miniatura" : "Tela cheia"}
          >
            {full ? <IconShrink size={15} /> : <IconExpand size={15} />}
          </button>
          <button
            type="button"
            className="screen-view__btn screen-view__btn--stop"
            onClick={onStop}
            title="Parar de assistir"
            aria-label="Parar de assistir"
          >
            ✕
          </button>
        </div>
      </div>
      {track ? (
        // Duplo-clique no vídeo troca miniatura⇄tela cheia — o gesto natural.
        <video ref={video} className="screen-view__video" autoPlay playsInline muted onDoubleClick={onToggleFull} />
      ) : (
        <div className="screen-view__idle">A tela terminou.</div>
      )}
    </div>
  );
}
